import { networkApiCallOnlyData } from '../lib/networking';
import { useCallback, useMemo } from 'react';
import {
  UseModelIndexResults,
  UseModelShowResults,
  useModelCreate,
  useModelDelete,
  useModelIndex,
  useModelKeyShow,
  useModelPathMember,
  useModelPathMemberBuild,
  useModelShow,
  useModelUpdate
} from './queries';
import {
  RhinoRecord,
  RhinoRecordIdentifier,
  RhinoResource,
  RhinoResourceSpecifier,
  RhinoResourceSpecifierToResource
} from '../index';
import { UseMutationResult } from '@tanstack/react-query';

// The query/mutation hooks are passed in polymorphically (useModelCreate,
// useModelShow, ...); their generic signatures don't unify, so the hook
// parameters are intentionally loose. The wrappers below re-assert the result
// shape so callers still get a real mutation/query type.
type ActionHookOptions = Record<string, unknown>;
type MutationActionHook = (
  model: RhinoResourceSpecifier,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any
) => unknown;
type QueryActionHook = (
  model: RhinoResourceSpecifier,
  id: RhinoRecordIdentifier,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any
) => unknown;

/* An action is called with its own arguments rather than with a resource, so
 * the only key it must carry is the id the member path is built from. */
export type RhinoActionVariables = RhinoRecord & {
  id: RhinoRecordIdentifier;
};

export type UseModelActionMutationResults<T> = UseMutationResult<
  RhinoResourceSpecifierToResource<T>,
  Error,
  RhinoActionVariables
> & {
  model: RhinoResource;
  resource: RhinoResourceSpecifierToResource<T> | null;
};

// Calls a member action, /api/<model>/:id/<action>. The endpoint renders the
// record it acted on (permit_and_render), as CRUD endpoints do: the default
// onSuccess refreshes that record by the id in the response. A caller that
// passes its own onSuccess takes over the refresh.
export const useModelMutationAction = <T extends RhinoResourceSpecifier>(
  model: T,
  action: string,
  useMutationAction: MutationActionHook,
  method: string,
  mutationOptions: ActionHookOptions = {}
): UseModelActionMutationResults<T> => {
  const build = useModelPathMemberBuild(model);

  const mutationFn = useCallback(
    (data: RhinoActionVariables) => {
      const endpoint = `${build(data)}/${action}`;

      return networkApiCallOnlyData(endpoint, { method, data });
    },
    [action, build, method]
  );

  const mutation = useMutationAction(model, {
    mutationFn,
    ...mutationOptions
  });

  return mutation as UseModelActionMutationResults<T>;
};

export const useModelQueryAction = <TResults extends object>(
  model: RhinoResourceSpecifier,
  id: RhinoRecordIdentifier,
  action: string,
  useQueryAction: QueryActionHook,
  {
    queryOptions = {},
    networkOptions = {},
    ...restOptions
  }: {
    queryOptions?: Record<string, unknown>;
    networkOptions?: Record<string, unknown>;
    [key: string]: unknown;
  }
): TResults => {
  const baseEndpoint = useModelPathMember(model, { id });
  const endpoint = useMemo(
    () => `${baseEndpoint}/${action}`,
    [action, baseEndpoint]
  );

  // This looks like the useModelShow key with the addition of the action
  const extraQueryKeys = useMemo(
    () => ({ ...queryOptions, ...networkOptions, ...restOptions }),
    [queryOptions, networkOptions, restOptions]
  );
  const queryKey = useModelKeyShow(model, id, [action, extraQueryKeys]);

  // The endpoint is of the form /api/<model>/<model-id>/<action>
  const queryFn = useCallback(
    ({ signal }: { signal?: AbortSignal }) =>
      networkApiCallOnlyData(endpoint, { ...networkOptions, signal }),
    [endpoint, networkOptions]
  );

  const query = useQueryAction(model, id, {
    queryOptions: {
      queryFn,
      queryKey,
      ...queryOptions
    },
    ...restOptions
  });

  return query as TResults;
};

export const useModelActionCreate = <T extends RhinoResourceSpecifier>(
  model: T,
  action: string,
  mutationOptions: ActionHookOptions = {}
): UseModelActionMutationResults<T> => {
  const mutation = useModelMutationAction(
    model,
    action,
    useModelCreate,
    'post',
    mutationOptions
  );

  return mutation;
};

export const useModelActionUpdate = <T extends RhinoResourceSpecifier>(
  model: T,
  action: string,
  mutationOptions: ActionHookOptions = {}
): UseModelActionMutationResults<T> => {
  const mutation = useModelMutationAction(
    model,
    action,
    useModelUpdate,
    'patch',
    mutationOptions
  );

  return mutation;
};

// FIXME - this won't quite work, but useModelDelete should be fixed to take both an id and object for deletion
// instead of just an id
export const useModelActionDelete = <T extends RhinoResourceSpecifier>(
  model: T,
  action: string,
  mutationOptions: ActionHookOptions = {}
): UseModelActionMutationResults<T> => {
  const mutation = useModelMutationAction(
    model,
    action,
    useModelDelete,
    'delete',
    mutationOptions
  );

  return mutation;
};

export const useModelActionShow = <T extends RhinoResourceSpecifier>(
  model: T,
  id: RhinoRecordIdentifier,
  action: string,
  options: Record<string, unknown> = {}
): UseModelShowResults<T> => {
  const query = useModelQueryAction<UseModelShowResults<T>>(
    model,
    id,
    action,
    useModelShow,
    options
  );

  return query;
};

// We must drop the 'id' parameter
export const useModelIndexActionWrapper = (
  model: RhinoResourceSpecifier,
  _id: RhinoRecordIdentifier,
  options: Record<string, unknown>
) => useModelIndex(model, options);

export const useModelActionIndex = <T extends RhinoResourceSpecifier>(
  model: T,
  id: RhinoRecordIdentifier,
  action: string,
  options: Record<string, unknown> = {}
): UseModelIndexResults<T> => {
  const query = useModelQueryAction<UseModelIndexResults<T>>(
    model,
    id,
    action,
    useModelIndexActionWrapper,
    options
  );

  return query;
};
